import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  TextField,
  IconButton,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ReceiptLong as ReceiptLongIcon,
  MonetizationOn as MonetizationIcon,
  TrendingUp as TrendingUpIcon,
  Savings as SavingsIcon,
  AttachMoney as ProfitIcon,
  CalendarToday as CalendarTodayIcon,
  EventNote as EventNoteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as PaidIcon,
  Assessment as AssessmentIcon,
  Payments as PaymentsIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import debounce from 'lodash/debounce';
import ResponsiveAppBar from '../components/AppBar';
import DeleteWithPassword from '../components/deleteWithPassword';
import RoleGuard from '../components/RoleGuard';
import { API_BASE_URL } from '../api/config';

// Centralized Theme configuration matching the Velavan Finance design language
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Velavan Teal
    secondary: { main: '#936097' }, // Velavan Plum
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: { main: '#ef4444' },
    success: { main: '#22c55e' },
    warning: { main: '#f59e0b' },
  },
  typography: {
    fontFamily: '"Outfit", "Poppins", "Roboto", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      color: '#1e293b',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.3px',
      color: '#1e293b',
    },
    h6: {
      fontWeight: 700,
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1rem',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 22px',
          boxShadow: '0 4px 12px rgba(147, 96, 151, 0.08)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(119, 200, 199, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.03)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          background: '#ffffff',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 12px 30px rgba(119, 200, 199, 0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          boxShadow: '0 20px 45px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '14px !important',
          marginBottom: 16,
          border: '1px solid rgba(226, 232, 240, 0.9)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.02)',
          '&:before': {
            display: 'none',
          },
          '&.nested': {
            marginBottom: 12,
            marginLeft: 8,
            marginRight: 8,
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
          },
        },
      },
    },
  },
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: theme.spacing(3.5),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(226, 232, 240, 0.9)',
  borderLeft: '5px solid #936097',
  borderBottom: '5px solid #77c8c7',
}));

const StyledSubtotalsPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '14px',
  padding: theme.spacing(2.5),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)',
  border: '1px solid rgba(226, 232, 240, 0.9)',
  borderLeft: '5px solid #936097',
  borderBottom: '5px solid #77c8c7',
  marginTop: theme.spacing(1.5),
}));

const MetricItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: '#77c8c7',
    boxShadow: '0 6px 16px rgba(119, 200, 199, 0.15)',
  },
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

// FinalMonthlyCalculations Component
const FinalMonthlyCalculations = ({ selectedMonth, calculateMonthlyAveragePendingDue }) => {
  const metrics = [
    { label: 'Total Fund Released', value: `₹${(selectedMonth.monthlyTotals?.totalFundReleasedToF2F ?? 0).toLocaleString('en-IN')}`, icon: <MonetizationIcon sx={{ color: '#0f766e', fontSize: 20 }} /> },
    { label: 'Total Loans', value: selectedMonth.monthlyTotals?.totalNoOfLoans ?? 0, icon: <ReceiptLongIcon sx={{ color: '#936097', fontSize: 20 }} /> },
    { label: 'Total Loan Amount', value: `₹${(selectedMonth.monthlyTotals?.totalLoanAmount ?? 0).toLocaleString('en-IN')}`, icon: <AccountIcon sx={{ color: '#0f766e', fontSize: 20 }} /> },
    { label: 'Total Principal In Due', value: `₹${(selectedMonth.monthlyTotals?.totalPrincipalInDue ?? 0).toLocaleString('en-IN')}`, icon: <SavingsIcon sx={{ color: '#b45309', fontSize: 20 }} /> },
    { label: 'Total Interest In Due', value: `₹${(selectedMonth.monthlyTotals?.totalInterestInDue ?? 0).toLocaleString('en-IN')}`, icon: <TrendingUpIcon sx={{ color: '#936097', fontSize: 20 }} /> },
    { label: 'Total Due Amount Paid', value: `₹${(selectedMonth.monthlyTotals?.totalDueAmountPaid ?? 0).toLocaleString('en-IN')}`, icon: <PaidIcon sx={{ color: '#15803d', fontSize: 20 }} /> },
    { label: 'Average Pending Due', value: ` ${(calculateMonthlyAveragePendingDue(selectedMonth.dailyEntries) ?? 0).toLocaleString('en-IN')}`, icon: <ReceiptIcon sx={{ color: '#b45309', fontSize: 20 }} /> },
    { label: 'Total Refreshment Expenses', value: `₹${(selectedMonth.monthlyTotals?.totalRefreshmentExpenses ?? 0).toLocaleString('en-IN')}`, icon: <PaymentsIcon sx={{ color: '#64748b', fontSize: 20 }} /> },
    { label: 'Total Other Expenses', value: `₹${(selectedMonth.monthlyTotals?.totalOtherExpenses ?? 0).toLocaleString('en-IN')}`, icon: <PaymentsIcon sx={{ color: '#64748b', fontSize: 20 }} /> },
    { label: 'Total Doc Charges', value: `₹${(selectedMonth.monthlyTotals?.totalDocCharge ?? 0).toLocaleString('en-IN')}`, icon: <ProfitIcon sx={{ color: '#0f766e', fontSize: 20 }} /> },
    { label: 'Total Profit Withdrawn', value: `₹${(selectedMonth.monthlyTotals?.totalProfitWithdrawn ?? 0).toLocaleString('en-IN')}`, icon: <ProfitIcon sx={{ color: '#936097', fontSize: 20 }} /> },
    { label: 'Final Closing Balance', value: `₹${(selectedMonth.monthlyTotals?.totalClosingBalance ?? 0).toLocaleString('en-IN')}`, isBold: true, icon: <ProfitIcon sx={{ color: '#15803d', fontSize: 20 }} /> },
  ];

  return (
    <StyledPaper elevation={0} sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#0f766e', display: 'flex' }}>
          <AssessmentIcon />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
            Final Monthly Calculations
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            Comprehensive financial rollup for {selectedMonth.summaryMonth}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MetricItem
              sx={
                metric.isBold
                  ? {
                      bgcolor: 'rgba(34, 197, 94, 0.08)',
                      borderColor: 'rgba(34, 197, 94, 0.4)',
                    }
                  : {}
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                {metric.icon}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: metric.isBold ? 700 : 600,
                    color: metric.isBold ? '#15803d' : '#475569',
                    fontSize: '0.875rem',
                  }}
                >
                  {metric.label}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: metric.isBold ? 800 : 700,
                  color: metric.isBold ? '#15803d' : '#1e293b',
                  fontSize: metric.isBold ? '1.05rem' : '0.925rem',
                }}
              >
                {metric.value}
              </Typography>
            </MetricItem>
          </Grid>
        ))}
      </Grid>
    </StyledPaper>
  );
};

// API URL
const API_URL = `${API_BASE_URL}/accounts`;

function DailyAccounts() {
  const navigate = useNavigate();
  // State variables
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState('');

  // Dialog states
  const [monthDialog, setMonthDialog] = useState(false);
  const [dailyEntryDialog, setDailyEntryDialog] = useState(false);

  // Form states
  const [monthForm, setMonthForm] = useState({
    summaryMonth: '',
    monthlyTotals: {
      totalFundReleasedToF2F: 0,
      totalNoOfLoans: 0,
      totalLoanAmount: 0,
      totalPrincipalInDue: 0,
      totalInterestInDue: 0,
      totalDueAmountPaid: 0,
      totalPendingDue: 0,
      totalRefreshmentExpenses: 0,
      totalOtherExpenses: 0,
      totalClosingBalance: 0,
      totalDocCharge: 0,
      totalProfitWithdrawn: 0,
    },
  });

  const [dailyEntryForm, setDailyEntryForm] = useState({
    date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'),
    openingBalance: 0,
    fundReleasedToF2F: 0,
    noOfLoans: 0,
    loanAmount: 0,
    principalInDue: 0,
    interestInDue: 0,
    pendingDue: 0,
    refreshmentExpenses: 0,
    otherExpenses: 0,
    otherExpensePurpose: '',
    closingBalance: 0,
    docCharge: 0,
    profitWithdrawn: 0,
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [monthFormError, setMonthFormError] = useState('');

  // Valid months for validation
  const validMonths = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  // Compute dueAmountPaid dynamically for display in the dialog
  const computeDueAmountPaid = () => {
    return (Number(dailyEntryForm.principalInDue) || 0) + (Number(dailyEntryForm.interestInDue) || 0);
  };

  // Data Grid columns for daily entries
  const columns = [
    {
      field: 'date',
      headerName: 'Date',
      width: 130,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(119, 200, 199, 0.15)',
            color: '#0f766e',
            fontWeight: 700,
            borderRadius: 1.5,
          }}
        />
      ),
    },
    {
      field: 'openingBalance',
      headerName: 'Opening Balance',
      width: 160,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'fundReleasedToF2F',
      headerName: 'Fund Released',
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#936097', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'noOfLoans',
      headerName: 'Loans Count',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ?? 0}
          size="small"
          sx={{ bgcolor: '#f1f5f9', color: '#1e293b', fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'loanAmount',
      headerName: 'Loan Amount',
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#0f766e', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'principalInDue',
      headerName: 'Principal In Due',
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'interestInDue',
      headerName: 'Interest In Due',
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#936097', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'dueAmountPaid',
      headerName: 'Due Amount Paid',
      width: 160,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#15803d', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'pendingDue',
      headerName: 'Pending Due',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={(params.value ?? 0).toLocaleString('en-IN')}
          size="small"
          sx={{ bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#b45309', fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'refreshmentExpenses',
      headerName: 'Refreshment Exp.',
      width: 160,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 500, color: '#64748b', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'otherExpenses',
      headerName: 'Other Expenses',
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 500, color: '#64748b', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'otherExpensePurpose',
      headerName: 'Other Expense Purpose',
      width: 180,
      renderCell: (params) => (
        <Typography sx={{ fontStyle: params.value ? 'normal' : 'italic', color: params.value ? '#1e293b' : '#94a3b8', fontSize: '0.85rem' }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'docCharge',
      headerName: 'Doc Charge',
      width: 130,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#0f766e', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'profitWithdrawn',
      headerName: 'Profit Withdrawn',
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#936097', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'closingBalance',
      headerName: 'Closing Balance',
      width: 160,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 800, color: '#15803d', fontSize: '0.875rem' }}>
          ₹{(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
          <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
            <Tooltip title="Edit Entry">
              <IconButton
                size="small"
                onClick={() => handleEditEntry(params.row)}
                sx={{
                  color: '#77c8c7',
                  bgcolor: 'rgba(119, 200, 199, 0.1)',
                  '&:hover': { bgcolor: 'rgba(119, 200, 199, 0.25)' },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </RoleGuard>
          <RoleGuard allowedRoles={["Admin Manager"]}>
            <DeleteWithPassword
              resourceId={params.row.date}
              resourceName="Daily Entry"
              onDelete={deleteDailyEntry}
              navigate={navigate}
              renderTrigger={(onClick) => (
                <Tooltip title="Delete Entry">
                  <IconButton
                    size="small"
                    onClick={onClick}
                    sx={{
                      color: '#ef4444',
                      bgcolor: 'rgba(239, 68, 68, 0.1)',
                      '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.25)' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            />
          </RoleGuard>
        </Box>
      ),
    },
  ];

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  // Fetch all account summaries on mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Handle search query with debounce
  const debouncedSetFilteredAccounts = useCallback(
    debounce((query, accountsList) => {
      setFilteredAccounts(
        accountsList.filter((account) =>
          account.summaryMonth.toLowerCase().includes(query.toLowerCase())
        )
      );
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetFilteredAccounts(searchQuery, accounts);
  }, [accounts, searchQuery, debouncedSetFilteredAccounts]);

  // Handle main tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setSelectedMonth(null);
    }
  };

  // Fetch all account summaries
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/get_all_summaries`);
      const validAccounts = res.data.filter((account) => validateSummaryMonth(account.summaryMonth) === '');
      if (validAccounts.length < res.data.length) {
        showAlert('Some summaries have invalid Month/Year values and were excluded', 'warning');
      }
      setAccounts(validAccounts);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching accounts');
      showAlert('Error fetching accounts', 'error');
      setLoading(false);
    }
  };

  // Fetch specific month summary
  const fetchMonthSummary = async (summaryMonth) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/get_summary_by_month/${summaryMonth}`);
      setSelectedMonth(res.data);
      setTabValue(1);
      setLoading(false);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching month summary');
      showAlert('Error fetching month summary', 'error');
      setLoading(false);
      return null;
    }
  };

  // Validate summaryMonth format
  const validateSummaryMonth = (value) => {
    if (!value) {
      return 'Month name is required';
    }
    const [month, year] = value.split(' ');
    if (!month || !year) {
      return 'Format must be "MMM YYYY" (e.g., April 2025 or Apr 2025)';
    }
    if (!validMonths.includes(month)) {
      return 'Month must be a valid abbreviation (e.g., Jan, Feb, ..., Dec)';
    }
    if (!/^\d{4}$/.test(year)) {
      return 'Year must be a four-digit number (e.g., 2025)';
    }
    return '';
  };

  // Create new month entry
  const createMonth = async () => {
    const err = validateSummaryMonth(monthForm.summaryMonth);
    if (err) {
      setMonthFormError(err);
      showAlert(err, 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/create_new_month_entry`, monthForm);
      setAccounts([...accounts, res.data]);
      setMonthDialog(false);
      resetMonthForm();
      showAlert('Month created successfully', 'success');
    } catch (errCatch) {
      setError(errCatch.response?.data?.message || 'Error creating month entry');
      showAlert(errCatch.response?.data?.message || 'Error creating month entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Add daily entry to month
  const addDailyEntry = async () => {
    if (!dailyEntryForm.date) {
      showAlert('Date is required', 'error');
      return;
    }

    const numericFields = [
      'openingBalance',
      'fundReleasedToF2F',
      'noOfLoans',
      'loanAmount',
      'principalInDue',
      'interestInDue',
      'pendingDue',
      'refreshmentExpenses',
      'otherExpenses',
      'closingBalance',
      'docCharge',
      'profitWithdrawn',
    ];

    for (const field of numericFields) {
      if (typeof dailyEntryForm[field] !== 'number') {
        showAlert(`${field} must be a valid number`, 'error');
        return;
      }
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/add_newDaily_entry_to_specific_month/${selectedMonth.summaryMonth}/daily`,
        dailyEntryForm
      );
      await fetchMonthSummary(selectedMonth.summaryMonth);
      setDailyEntryDialog(false);
      resetDailyEntryForm();
      showAlert('Daily entry added successfully', 'success');
    } catch (errCatch) {
      setError(errCatch.response?.data?.message || 'Error adding daily entry');
      showAlert(errCatch.response?.data?.message || 'Error adding daily entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update daily entry
  const updateDailyEntry = async () => {
    if (!dailyEntryForm.date) {
      showAlert('Date is required', 'error');
      return;
    }
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/update_daily_entry_by_date_inSpecific_month/${selectedMonth.summaryMonth}/daily/${editDate}`,
        { dailyData: dailyEntryForm }
      );
      await fetchMonthSummary(selectedMonth.summaryMonth);
      setDailyEntryDialog(false);
      resetDailyEntryForm();
      setIsEditMode(false);
      showAlert('Daily entry updated successfully', 'success');
    } catch (errCatch) {
      setError(errCatch.response?.data?.message || 'Error updating daily entry');
      showAlert(errCatch.response?.data?.message || 'Error updating daily entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete daily entry
  const deleteDailyEntry = async (date) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/delete_entry_by_date/${selectedMonth.summaryMonth}/${date}`);
      await fetchMonthSummary(selectedMonth.summaryMonth);
    } catch (errCatch) {
      setError(errCatch.response?.data?.message || 'Error deleting daily entry');
      throw errCatch;
    } finally {
      setLoading(false);
    }
  };

  // Delete month summary
  const deleteMonth = async (summaryMonth) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/delete_entire_month_summary/${summaryMonth}`);
      setAccounts(accounts.filter((account) => account.summaryMonth !== summaryMonth));
      if (selectedMonth && selectedMonth.summaryMonth === summaryMonth) {
        setSelectedMonth(null);
        setTabValue(0);
      }
    } catch (errCatch) {
      setError(errCatch.response?.data?.message || 'Error deleting month');
      throw errCatch;
    } finally {
      setLoading(false);
    }
  };

  // Reset forms
  const resetMonthForm = () => {
    setMonthForm({
      summaryMonth: '',
      monthlyTotals: {
        totalFundReleasedToF2F: 0,
        totalNoOfLoans: 0,
        totalLoanAmount: 0,
        totalPrincipalInDue: 0,
        totalInterestInDue: 0,
        totalDueAmountPaid: 0,
        totalPendingDue: 0,
        totalRefreshmentExpenses: 0,
        totalOtherExpenses: 0,
        totalClosingBalance: 0,
        totalDocCharge: 0,
        totalProfitWithdrawn: 0,
      },
    });
    setMonthFormError('');
  };

  const resetDailyEntryForm = () => {
    let openingBalance = 0;
    if (selectedMonth?.dailyEntries?.length > 0) {
      const sortedEntries = [...selectedMonth.dailyEntries].sort((a, b) => {
        const dateA = new Date(a.date.split('-').reverse().join('-'));
        const dateB = new Date(b.date.split('-').reverse().join('-'));
        return dateB - dateA;
      });
      openingBalance = sortedEntries[0].closingBalance ?? 0;
    }

    setDailyEntryForm({
      date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'),
      openingBalance,
      fundReleasedToF2F: 0,
      noOfLoans: 0,
      loanAmount: 0,
      principalInDue: 0,
      interestInDue: 0,
      pendingDue: 0,
      refreshmentExpenses: 0,
      otherExpenses: 0,
      otherExpensePurpose: '',
      closingBalance: 0,
      docCharge: 0,
      profitWithdrawn: 0,
    });
    setIsEditMode(false);
    setEditDate('');
  };

  // Show alert
  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
  };

  // Edit daily entry
  const handleEditEntry = (entry) => {
    setDailyEntryForm({
      date: entry.date,
      openingBalance: entry.openingBalance ?? 0,
      fundReleasedToF2F: entry.fundReleasedToF2F ?? 0,
      noOfLoans: entry.noOfLoans ?? 0,
      loanAmount: entry.loanAmount ?? 0,
      principalInDue: entry.principalInDue ?? 0,
      interestInDue: entry.interestInDue ?? 0,
      pendingDue: entry.pendingDue ?? 0,
      refreshmentExpenses: entry.refreshmentExpenses ?? 0,
      otherExpenses: entry.otherExpenses ?? 0,
      otherExpensePurpose: entry.otherExpensePurpose ?? '',
      closingBalance: entry.closingBalance ?? 0,
      docCharge: entry.docCharge ?? 0,
      profitWithdrawn: entry.profitWithdrawn ?? 0,
    });
    setIsEditMode(true);
    setEditDate(entry.date);
    setDailyEntryDialog(true);
  };

  // Handle form input changes
  const handleMonthFormChange = (e) => {
    const { name, value } = e.target;
    setMonthForm({ ...monthForm, [name]: value });
    setMonthFormError(validateSummaryMonth(value));
  };

  const handleDailyEntryFormChange = (e) => {
    const { name, value } = e.target;
    setDailyEntryForm({
      ...dailyEntryForm,
      [name]: name === 'otherExpensePurpose' || name === 'date' ? value : Number(value) || value,
    });
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Helper function to group entries by week
  const groupEntriesByWeek = (entries) => {
    const sortedEntries = [...entries].sort((a, b) => {
      const dateA = new Date(a.date.split('-').reverse().join('-'));
      const dateB = new Date(b.date.split('-').reverse().join('-'));
      return dateA - dateB;
    });

    const weeks = [];
    const weekRanges = [
      { start: 1, end: 7, weekNumber: 1 },
      { start: 8, end: 14, weekNumber: 2 },
      { start: 15, end: 22, weekNumber: 3 },
      { start: 23, end: 31, weekNumber: 4 },
    ];

    weekRanges.forEach((range) => {
      const weekEntries = sortedEntries.filter((entry) => {
        const day = parseInt(entry.date.split('-')[0], 10);
        return day >= range.start && (range.weekNumber === 4 ? true : day <= range.end);
      });

      if (weekEntries.length > 0) {
        weeks.push({
          weekNumber: range.weekNumber,
          startDay: range.start,
          endDay: range.weekNumber === 4 ? Math.max(...weekEntries.map((e) => parseInt(e.date.split('-')[0], 10))) : range.end,
          entries: weekEntries,
          totals: calculateWeekTotals(weekEntries),
          averagePendingDue: calculateAveragePendingDue(weekEntries),
        });
      }
    });

    return weeks;
  };

  // Helper function to calculate weekly totals
  const calculateWeekTotals = (weekEntries) => {
    return weekEntries.reduce(
      (totals, entry) => ({
        totalFundReleasedToF2F: totals.totalFundReleasedToF2F + (entry.fundReleasedToF2F ?? 0),
        totalNoOfLoans: totals.totalNoOfLoans + (entry.noOfLoans ?? 0),
        totalLoanAmount: totals.totalLoanAmount + (entry.loanAmount ?? 0),
        totalPrincipalInDue: totals.totalPrincipalInDue + (entry.principalInDue ?? 0),
        totalInterestInDue: totals.totalInterestInDue + (entry.interestInDue ?? 0),
        totalDueAmountPaid: totals.totalDueAmountPaid + (entry.dueAmountPaid ?? 0),
        totalPendingDue: totals.totalPendingDue + (entry.pendingDue ?? 0),
        totalRefreshmentExpenses: totals.totalRefreshmentExpenses + (entry.refreshmentExpenses ?? 0),
        totalOtherExpenses: totals.totalOtherExpenses + (entry.otherExpenses ?? 0),
        totalDocCharge: totals.totalDocCharge + (entry.docCharge ?? 0),
        totalProfitWithdrawn: totals.totalProfitWithdrawn + (entry.profitWithdrawn ?? 0),
        totalClosingBalance: entry.closingBalance ?? 0,
      }),
      {
        totalFundReleasedToF2F: 0,
        totalNoOfLoans: 0,
        totalLoanAmount: 0,
        totalPrincipalInDue: 0,
        totalInterestInDue: 0,
        totalDueAmountPaid: 0,
        totalPendingDue: 0,
        totalRefreshmentExpenses: 0,
        totalOtherExpenses: 0,
        totalDocCharge: 0,
        totalProfitWithdrawn: 0,
        totalClosingBalance: 0,
      }
    );
  };

  // Helper function to calculate average pending due for a week
  const calculateAveragePendingDue = (weekEntries) => {
    if (weekEntries.length === 0) return 0;
    const totalPendingDue = weekEntries.reduce((sum, entry) => sum + (entry.pendingDue ?? 0), 0);
    return totalPendingDue / weekEntries.length;
  };

  // Helper function to calculate overall average pending due for the month
  const calculateMonthlyAveragePendingDue = (entries) => {
    if (entries.length === 0) return 0;
    const totalPendingDue = entries.reduce((sum, entry) => sum + (entry.pendingDue ?? 0), 0);
    return totalPendingDue / entries.length;
  };

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="xl">
            {/* Header Section */}
            <Box
              sx={{
                mb: 4,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.6,
                    borderRadius: '50px',
                    bgcolor: 'rgba(119, 200, 199, 0.15)',
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#77c8c7' }} />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: '#936097', textTransform: 'uppercase', letterSpacing: '0.8px' }}
                  >
                    Daily Accounts Ledger
                  </Typography>
                </Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                  Daily Account Management System
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                  Comprehensive financial ledger tracking daily transactions, cash flows, weekly subtotals & monthly closing balances
                </Typography>
              </Box>

              {/* Navigation Module Dropdown */}
              <FormControl
                size="small"
                sx={{
                  minWidth: 230,
                  bgcolor: '#ffffff',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                }}
              >
                <InputLabel sx={{ fontWeight: 500, color: '#64748b' }}>Navigate Module</InputLabel>
                <Select
                  value={selectedPage}
                  label="Navigate Module"
                  onChange={handleNavigate}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#77c8c7' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#936097' },
                  }}
                >
                  <MenuItem value="/admin">Gold & Vehicle Loan</MenuItem>
                  <MenuItem value="/velavan-finance">Velavan Finance Ledger</MenuItem>
                  <MenuItem value="/due-matters">Due Matters</MenuItem>
                  <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                  <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                  <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                  <MenuItem value="/monthly-graph">Monthly Graph Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Custom Tab Switcher */}
            <Box sx={{ mb: 4, borderBottom: '1px solid rgba(226, 232, 240, 0.9)' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="account tabs"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    minWidth: 140,
                    color: '#64748b',
                    '&.Mui-selected': {
                      color: '#1e293b',
                    },
                  },
                }}
              >
                <Tab icon={<CalendarTodayIcon sx={{ fontSize: 18, mr: 0.8 }} />} iconPosition="start" label="All Months Overview" />
                {selectedMonth && (
                  <Tab
                    icon={<ReceiptLongIcon sx={{ fontSize: 18, mr: 0.8 }} />}
                    iconPosition="start"
                    label={`${selectedMonth.summaryMonth} Ledger Details`}
                  />
                )}
              </Tabs>
            </Box>

            {/* All Months View (Tab 0) */}
            {tabValue === 0 && (
              <Box>
                {/* Search & Actions Bar */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search month (e.g., Apr 2025)..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#936097', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: { xs: '100%', sm: 320 },
                      bgcolor: '#ffffff',
                      borderRadius: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '& fieldset': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                        '&:hover fieldset': { borderColor: '#77c8c7' },
                        '&.Mui-focused fieldset': { borderColor: '#936097' },
                      },
                    }}
                  />

                  <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setMonthDialog(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                        color: '#ffffff',
                        width: { xs: '100%', sm: 'auto' },
                        px: 3,
                        py: 1.2,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #66b5b4 0%, #804f84 100%)',
                        },
                      }}
                    >
                      Add New Month
                    </Button>
                  </RoleGuard>
                </Box>

                {loading ? (
                  <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress sx={{ color: '#77c8c7' }} />
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Loading Monthly Summaries...
                    </Typography>
                  </Box>
                ) : filteredAccounts.length === 0 ? (
                  <Paper
                    sx={{
                      p: 5,
                      textAlign: 'center',
                      bgcolor: '#ffffff',
                      borderRadius: 4,
                      border: '1px solid rgba(226, 232, 240, 0.9)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'rgba(119, 200, 199, 0.15)',
                        color: '#0f766e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <CalendarTodayIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {searchQuery ? 'No matching months found' : 'No monthly summaries found'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, maxWidth: 450, mx: 'auto' }}>
                      {searchQuery
                        ? 'Try searching with a different month name or year format.'
                        : 'Click the "Add New Month" button to create your first monthly accounting ledger.'}
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {filteredAccounts.map((account) => (
                      <Grid item xs={12} sm={6} md={4} key={account.summaryMonth}>
                        <Card
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Top colored accent line */}
                          <Box
                            sx={{
                              height: 4,
                              background: 'linear-gradient(90deg, #77c8c7 0%, #936097 100%)',
                            }}
                          />
                          <CardContent sx={{ p: 3, flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                {account.summaryMonth}
                              </Typography>
                              <Chip
                                icon={<EventNoteIcon sx={{ fontSize: '14px !important', color: '#0f766e !important' }} />}
                                label={`${account.dailyEntries ? account.dailyEntries.length : 0} entries`}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(119, 200, 199, 0.15)',
                                  color: '#0f766e',
                                  fontWeight: 700,
                                  borderRadius: 1.5,
                                }}
                              />
                            </Box>

                            <Grid container spacing={1.5} sx={{ mt: 1 }}>
                              <Grid item xs={6}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
                                    Total Loans
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#1e293b', mt: 0.2 }}>
                                    {account.monthlyTotals?.totalNoOfLoans ?? 0}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
                                    Loan Amount
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#0f766e', mt: 0.2 }}>
                                    ₹{(account.monthlyTotals?.totalLoanAmount ?? 0).toLocaleString('en-IN')}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                                    border: '1px solid rgba(34, 197, 94, 0.2)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700 }}>
                                    Final Closing Balance
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#15803d' }}>
                                    ₹{(account.monthlyTotals?.totalClosingBalance ?? 0).toLocaleString('en-IN')}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>

                          <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.8)' }} />

                          <Box sx={{ p: 2, bgcolor: '#ffffff' }}>
                            <Grid container spacing={1.5}>
                              <Grid item xs={8}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  endIcon={<ArrowForwardIcon fontSize="small" />}
                                  onClick={() => fetchMonthSummary(account.summaryMonth)}
                                  sx={{
                                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                                    color: '#ffffff',
                                    py: 1,
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #66b5b4 0%, #804f84 100%)',
                                    },
                                  }}
                                >
                                  View Details
                                </Button>
                              </Grid>
                              <Grid item xs={4}>
                                <RoleGuard allowedRoles={["Admin Manager"]}>
                                  <DeleteWithPassword
                                    resourceId={account.summaryMonth}
                                    resourceName="Month Summary"
                                    onDelete={deleteMonth}
                                    navigate={navigate}
                                    renderTrigger={(onClick) => (
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        fullWidth
                                        onClick={onClick}
                                        sx={{
                                          borderColor: 'rgba(239, 68, 68, 0.4)',
                                          bgcolor: 'rgba(239, 68, 68, 0.04)',
                                          color: '#ef4444',
                                          py: 1,
                                          fontSize: '0.875rem',
                                          borderRadius: 3,
                                          '&:hover': {
                                            bgcolor: 'rgba(239, 68, 68, 0.1)',
                                            borderColor: '#ef4444',
                                          },
                                        }}
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  />
                                </RoleGuard>
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Month Detail View (Tab 1) */}
            {tabValue === 1 && selectedMonth && (
              <Box>
                {/* Month Details Header Bar */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 4,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => {
                        setSelectedMonth(null);
                        setTabValue(0);
                      }}
                      sx={{
                        borderRadius: 3,
                        borderColor: 'rgba(226, 232, 240, 0.9)',
                        color: '#64748b',
                        bgcolor: '#ffffff',
                        '&:hover': {
                          borderColor: '#77c8c7',
                          bgcolor: 'rgba(119, 200, 199, 0.08)',
                          color: '#0f766e',
                        },
                      }}
                    >
                      All Months
                    </Button>
                    <Box>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {selectedMonth.summaryMonth} - Daily Ledger Breakdown
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Viewing weekly transaction entries and calculation subtotals
                      </Typography>
                    </Box>
                  </Box>

                  <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        resetDailyEntryForm();
                        setDailyEntryDialog(true);
                      }}
                      sx={{
                        background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                        color: '#ffffff',
                        px: 3,
                        py: 1.2,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #66b5b4 0%, #804f84 100%)',
                        },
                      }}
                    >
                      Add Daily Entry
                    </Button>
                  </RoleGuard>
                </Box>

                {/* Daily Entries and Subtotals in Accordions */}
                {selectedMonth.dailyEntries && selectedMonth.dailyEntries.length > 0 ? (
                  <Box>
                    {/* Accordion 1: All Weeks Entries */}
                    <Accordion defaultExpanded={true} sx={{ overflow: 'hidden' }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#0f766e' }} />}
                        aria-controls="all-weeks-content"
                        id="all-weeks-header"
                        sx={{
                          bgcolor: 'rgba(119, 200, 199, 0.08)',
                          px: 3,
                          py: 1,
                          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: 'rgba(119, 200, 199, 0.2)', color: '#0f766e', display: 'flex' }}>
                            <CalendarTodayIcon fontSize="small" />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                            Daily Entries (Grouped by Weeks: Days 1 to {selectedMonth.dailyEntries.length > 0 ? Math.max(...selectedMonth.dailyEntries.map((e) => parseInt(e.date.split('-')[0], 10))) : 'End'})
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: '#ffffff', p: { xs: 1.5, sm: 3 } }}>
                        {groupEntriesByWeek(selectedMonth.dailyEntries).map((week) => (
                          <Accordion
                            key={week.weekNumber}
                            defaultExpanded={false}
                            className="nested"
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: '#936097' }} />}
                              aria-controls={`week-${week.weekNumber}-content`}
                              id={`week-${week.weekNumber}-header`}
                              sx={{
                                bgcolor: '#f8fafc',
                                px: 2.5,
                                py: 0.5,
                                borderBottom: '1px solid rgba(226, 232, 240, 0.7)',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  Week {week.weekNumber} (Days {week.startDay} to {week.endDay})
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Chip
                                    label={`${week.entries.length} ${week.entries.length === 1 ? 'entry' : 'entries'}`}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', fontWeight: 700 }}
                                  />
                                  <Chip
                                    label={`Closing: ₹${(week.totals.totalClosingBalance ?? 0).toLocaleString('en-IN')}`}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#15803d', fontWeight: 700 }}
                                  />
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: '#ffffff', p: 2 }}>
                              <Box sx={{ height: 340, width: '100%' }}>
                                <DataGrid
                                  rows={week.entries.map((entry) => ({ id: entry.date, ...entry }))}
                                  columns={columns}
                                  pageSizeOptions={[5, 10, 20]}
                                  initialState={{
                                    pagination: { paginationModel: { pageSize: 5 } },
                                  }}
                                  disableRowSelectionOnClick
                                  sx={{
                                    borderRadius: 3,
                                    border: '1px solid rgba(226, 232, 240, 0.9)',
                                    '& .MuiDataGrid-columnHeaders': {
                                      bgcolor: '#f1f5f9',
                                      color: '#1e293b',
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                    },
                                    '& .MuiDataGrid-cell': {
                                      borderBottom: '1px solid #e5e7eb',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                      bgcolor: 'rgba(119, 200, 199, 0.08)',
                                    },
                                  }}
                                />
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Accordion 2: Subtotals of All Weeks */}
                    <Accordion defaultExpanded={true} sx={{ overflow: 'hidden' }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#936097' }} />}
                        aria-controls="subtotals-all-weeks-content"
                        id="subtotals-all-weeks-header"
                        sx={{
                          bgcolor: 'rgba(147, 96, 151, 0.08)',
                          px: 3,
                          py: 1,
                          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: 'rgba(147, 96, 151, 0.2)', color: '#936097', display: 'flex' }}>
                            <AssessmentIcon fontSize="small" />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                            Subtotals Summary of All Weeks
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: '#ffffff', p: { xs: 1.5, sm: 3 } }}>
                        {groupEntriesByWeek(selectedMonth.dailyEntries).map((week) => (
                          <Accordion
                            key={week.weekNumber}
                            defaultExpanded={false}
                            className="nested"
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: '#936097' }} />}
                              aria-controls={`subtotals-week-${week.weekNumber}-content`}
                              id={`subtotals-week-${week.weekNumber}-header`}
                              sx={{
                                bgcolor: '#f8fafc',
                                px: 2.5,
                                py: 0.5,
                                borderBottom: '1px solid rgba(226, 232, 240, 0.7)',
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                Week {week.weekNumber} (Days {week.startDay} to {week.endDay}) - Subtotals
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: '#ffffff', p: 2 }}>
                              <StyledSubtotalsPaper>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Fund Released</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f766e' }}>
                                        ₹{(week.totals.totalFundReleasedToF2F ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Total Loans</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                        {week.totals.totalNoOfLoans ?? 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Loan Amount</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f766e' }}>
                                        ₹{(week.totals.totalLoanAmount ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Principal In Due</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#334155' }}>
                                        ₹{(week.totals.totalPrincipalInDue ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Interest In Due</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#936097' }}>
                                        ₹{(week.totals.totalInterestInDue ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Due Amount Paid</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#15803d' }}>
                                        ₹{(week.totals.totalDueAmountPaid ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Average Pending Due</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#b45309' }}>
                                        {(week.averagePendingDue ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Refreshment Expenses</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#64748b' }}>
                                        ₹{(week.totals.totalRefreshmentExpenses ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Other Expenses</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#64748b' }}>
                                        ₹{(week.totals.totalOtherExpenses ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Doc Charges</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f766e' }}>
                                        ₹{(week.totals.totalDocCharge ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Profit Withdrawn</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#936097' }}>
                                        ₹{(week.totals.totalProfitWithdrawn ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(34, 197, 94, 0.08)',
                                        border: '1px solid rgba(34, 197, 94, 0.3)',
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700 }}>
                                        Closing Balance
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 800, color: '#15803d' }}>
                                        ₹{(week.totals.totalClosingBalance ?? 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </StyledSubtotalsPaper>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Final Monthly Calculations Component */}
                    <FinalMonthlyCalculations
                      selectedMonth={selectedMonth}
                      calculateMonthlyAveragePendingDue={calculateMonthlyAveragePendingDue}
                    />
                  </Box>
                ) : (
                  <Paper
                    sx={{
                      p: 5,
                      textAlign: 'center',
                      bgcolor: '#ffffff',
                      borderRadius: 4,
                      border: '1px solid rgba(226, 232, 240, 0.9)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'rgba(119, 200, 199, 0.15)',
                        color: '#0f766e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <ReceiptLongIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      No daily entries found for {selectedMonth.summaryMonth}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                      Click the "Add Daily Entry" button above to record your first transaction for this month.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Add New Month Dialog */}
            <Dialog
              open={monthDialog}
              onClose={() => {
                setMonthDialog(false);
                resetMonthForm();
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 800, color: '#1e293b', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ color: '#77c8c7' }} />
                  Add New Month Ledger
                </Box>
              </DialogTitle>
              <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.8)' }} />
              <DialogContent sx={{ pt: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                  Create a new accounting ledger period. Please use standard format e.g. <b>Apr 2025</b>.
                </Typography>
                <TextField
                  name="summaryMonth"
                  label="Month Name"
                  fullWidth
                  required
                  value={monthForm.summaryMonth}
                  onChange={handleMonthFormChange}
                  placeholder="Apr 2025"
                  error={!!monthFormError}
                  helperText={monthFormError || 'Enter as MMM YYYY (e.g., Apr 2025)'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      '& fieldset': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                      '&:hover fieldset': { borderColor: '#77c8c7' },
                      '&.Mui-focused fieldset': { borderColor: '#936097' },
                    },
                  }}
                />
              </DialogContent>
              <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                  onClick={() => {
                    setMonthDialog(false);
                    resetMonthForm();
                  }}
                  disabled={loading}
                  sx={{ color: '#64748b', borderRadius: 2.5 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={createMonth}
                  disabled={loading || !!monthFormError}
                  startIcon={loading && <CircularProgress size={18} sx={{ color: '#ffffff' }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    borderRadius: 2.5,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #66b5b4 0%, #804f84 100%)',
                    },
                  }}
                >
                  Create Month
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add / Edit Daily Entry Dialog */}
            <Dialog
              open={dailyEntryDialog}
              onClose={() => {
                setDailyEntryDialog(false);
                resetDailyEntryForm();
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 800, color: '#1e293b', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isEditMode ? <EditIcon sx={{ color: '#936097' }} /> : <AddIcon sx={{ color: '#77c8c7' }} />}
                  {isEditMode ? `Edit Daily Entry (${editDate})` : `Add Daily Entry for ${selectedMonth?.summaryMonth}`}
                </Box>
              </DialogTitle>
              <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.8)' }} />
              <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="date"
                      label="Entry Date"
                      type="text"
                      fullWidth
                      required
                      value={dailyEntryForm.date}
                      onChange={handleDailyEntryFormChange}
                      disabled={isEditMode}
                      placeholder="DD-MMM-YYYY (e.g., 01-Jan-2025)"
                      error={!dailyEntryForm.date && dailyEntryDialog}
                      helperText={!dailyEntryForm.date && dailyEntryDialog ? 'Date is required' : ''}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="openingBalance"
                      label="Opening Balance (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.openingBalance}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="fundReleasedToF2F"
                      label="Fund Released To VF (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.fundReleasedToF2F}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="noOfLoans"
                      label="Number of Loans"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.noOfLoans}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="loanAmount"
                      label="Loan Amount (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.loanAmount}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="principalInDue"
                      label="Principal In Due (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.principalInDue}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="interestInDue"
                      label="Interest In Due (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.interestInDue}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Due Amount Paid (Computed ₹)"
                      type="number"
                      fullWidth
                      value={computeDueAmountPaid()}
                      InputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          bgcolor: 'rgba(119, 200, 199, 0.08)',
                          '& fieldset': { borderColor: '#77c8c7' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="pendingDue"
                      label="Pending Due"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.pendingDue}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="refreshmentExpenses"
                      label="Refreshment Expenses (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.refreshmentExpenses}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="otherExpenses"
                      label="Other Expenses (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.otherExpenses}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="otherExpensePurpose"
                      label="Other Expense Purpose"
                      type="text"
                      fullWidth
                      value={dailyEntryForm.otherExpensePurpose}
                      onChange={handleDailyEntryFormChange}
                      placeholder="e.g. Stationery, Travel"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="docCharge"
                      label="Documentation Charge (₹)"
                      type="number"
                      fullWidth
                      value={200}
                      InputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          bgcolor: 'rgba(119, 200, 199, 0.08)',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="profitWithdrawn"
                      label="Profit Withdrawn (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.profitWithdrawn}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="closingBalance"
                      label="Closing Balance (₹)"
                      type="number"
                      fullWidth
                      value={dailyEntryForm.closingBalance}
                      onChange={handleDailyEntryFormChange}
                      inputProps={{ min: 0 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          '&:hover fieldset': { borderColor: '#77c8c7' },
                          '&.Mui-focused fieldset': { borderColor: '#936097' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                  onClick={() => {
                    setDailyEntryDialog(false);
                    resetDailyEntryForm();
                  }}
                  disabled={loading}
                  sx={{ color: '#64748b', borderRadius: 2.5 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={isEditMode ? updateDailyEntry : addDailyEntry}
                  disabled={loading || !dailyEntryForm.date}
                  startIcon={loading && <CircularProgress size={18} sx={{ color: '#ffffff' }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                    color: '#ffffff',
                    borderRadius: 2.5,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #66b5b4 0%, #804f84 100%)',
                    },
                  }}
                >
                  {isEditMode ? 'Update Entry' : 'Save Daily Entry'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar for alerts */}
            <Snackbar
              open={alert.open}
              autoHideDuration={6000}
              onClose={() => setAlert({ ...alert, open: false })}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setAlert({ ...alert, open: false })}
                severity={alert.severity}
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  fontWeight: 600,
                }}
              >
                {alert.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
}

export default DailyAccounts;